import React, { useEffect, useMemo, useRef, useState } from "react";
import { t, getLocale } from "../i18n";
import Sidebar from "../components/Sidebar.jsx";
import MediaSelector from "../components/MediaSelector.jsx";
import TagManager from "../components/TagManager.jsx";
import { notifyGenerationComplete } from "../services/notifications";
import { fetchBillingStatus, fetchCreditEstimate } from "../services/billing.js";
import "./VideoStudio.css";

const MODES = [
  { id: "text", label: "Texte -> Video" },
  { id: "image", label: "Image -> Video" },
  { id: "ref", label: "Images de Reference" },
  { id: "interp", label: "Interpolation (Debut/Fin)" },
  { id: "extend", label: "Extension Video" }
];

import { buildDownloadName, downloadFile } from "../utils/download";

export default function VideoStudio() {
  const storedState = useMemo(() => {
    try {
      const raw = window.sessionStorage.getItem("contentos_video_state");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const [mode, setMode] = useState(storedState.mode || "text");
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [configError, setConfigError] = useState("");
  const [models, setModels] = useState([]);
  const [ratios, setRatios] = useState([]);
  const [resolutions, setResolutions] = useState([]);
  const [durations, setDurations] = useState([4, 6, 8]);
  const [guide, setGuide] = useState({ groups: [], tags: [], presets: [] });

  const [modelId, setModelId] = useState(storedState.modelId || "");
  const [ratio, setRatio] = useState(storedState.ratio || "9:16");
  const [resolution, setResolution] = useState(storedState.resolution || "720p");
  const [duration, setDuration] = useState(storedState.duration || 4);
  const [promptText, setPromptText] = useState(storedState.promptText || "");
  const [selectedTags, setSelectedTags] = useState(new Set(storedState.selectedTags || []));
  const [selectedPresets, setSelectedPresets] = useState(storedState.selectedPresets || []);
  const [userPresets, setUserPresets] = useState([]);
  const [userPresetsLoading, setUserPresetsLoading] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [showPresetForm, setShowPresetForm] = useState(false);
  const [selectedPresetIds, setSelectedPresetIds] = useState(new Set());
  const [grain, setGrain] = useState(Number.isFinite(storedState.grain) ? storedState.grain : 15);
  const [contrast, setContrast] = useState(Number.isFinite(storedState.contrast) ? storedState.contrast : 60);
  const [billingStatus, setBillingStatus] = useState(null);
  const [creditEstimate, setCreditEstimate] = useState(1);
  const [billingError, setBillingError] = useState("");
  const [previewZoom, setPreviewZoom] = useState(1);
  const [frameEditMode, setFrameEditMode] = useState(false);
  const [frameRotation, setFrameRotation] = useState(0);
  const [frameContrast, setFrameContrast] = useState(100);
  const [frameCropMode, setFrameCropMode] = useState(false);
  const [frameCropRect, setFrameCropRect] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const [frameMeta, setFrameMeta] = useState({
    naturalW: 0,
    naturalH: 0,
    displayW: 0,
    displayH: 0,
    offsetX: 0,
    offsetY: 0
  });
  const [frameUrl, setFrameUrl] = useState("");
  const [frameMediaItemId, setFrameMediaItemId] = useState("");
  const [frameMediaVersionId, setFrameMediaVersionId] = useState("");
  const [frameEditSaving, setFrameEditSaving] = useState(false);
  const [frameEditError, setFrameEditError] = useState("");

  const [expertMode, setExpertMode] = useState(storedState.expertMode || "beginner");
  const [narrative, setNarrative] = useState(
    storedState.narrative || {
      subject: "",
      action: "",
      environment: "",
      temporal_progression: ""
    }
  );
  const [cinematography, setCinematography] = useState(
    storedState.cinematography || {
      shot_type: "",
      camera_motion: "",
      lens: "",
      focus: "",
      framing: ""
    }
  );
  const [visualStyle, setVisualStyle] = useState(
    storedState.visualStyle || {
      lighting: "",
      color_palette: "",
      mood: "",
      art_direction: ""
    }
  );
  const [constraints, setConstraints] = useState(
    storedState.constraints || {
      identity_consistency: true,
      no_style_drift: true,
      no_character_merge: true
    }
  );

  const [inputImage, setInputImage] = useState(storedState.inputImage || "");
  const [referenceImages, setReferenceImages] = useState(storedState.referenceImages || []);
  const [startImage, setStartImage] = useState(storedState.startImage || "");
  const [endImage, setEndImage] = useState(storedState.endImage || "");
  const [inputVideo, setInputVideo] = useState(storedState.inputVideo || "");
  const [grokEditVideoUrl, setGrokEditVideoUrl] = useState(storedState.grokEditVideoUrl || "");
  const [inputMediaVersionIdsByTarget, setInputMediaVersionIdsByTarget] = useState(
    storedState.inputMediaVersionIdsByTarget || {}
  );
  const [baseMediaItemId, setBaseMediaItemId] = useState(storedState.baseMediaItemId || "");
  const [baseMediaVersionId, setBaseMediaVersionId] = useState(storedState.baseMediaVersionId || "");

  const [activeProjectId, setActiveProjectId] = useState("");
  const [generating, setGenerating] = useState(false);
  const [resultUrl, setResultUrl] = useState(storedState.resultUrl || "");
  const [errorMsg, setErrorMsg] = useState("");
  const [compileReport, setCompileReport] = useState(null);
  const [compiledPrompt, setCompiledPrompt] = useState("");
  const [recentVideos, setRecentVideos] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [mediaThumbs, setMediaThumbs] = useState({});
  const [projectTags, setProjectTags] = useState([]);
  const [favoriteTags, setFavoriteTags] = useState([]);
  const [projectTagsLoading, setProjectTagsLoading] = useState(false);
  const [favoriteTagsLoading, setFavoriteTagsLoading] = useState(false);
  const appliedProjectRef = useRef("");
  const frameWrapRef = useRef(null);
  const frameImgRef = useRef(null);
  const frameCropDragRef = useRef(null);

  const markerByTag = useMemo(() => {
    const map = new Map();
    guide.tags.forEach((tag) => map.set(tag.id, tag.marker));
    return map;
  }, [guide.tags]);

  const tagLabelById = useMemo(() => {
    const map = new Map();
    guide.tags.forEach((tag) => {
      const label = tag.label?.fr || tag.label?.en || tag.label || tag.id;
      map.set(tag.id, label);
    });
    return map;
  }, [guide.tags]);

  useEffect(() => {
    let isMounted = true;
    async function loadConfig() {
      setLoadingConfig(true);
      setConfigError("");
      try {
        const locale = getLocale();
        const res = await fetch(`/api/fast/video/config?locale=${encodeURIComponent(locale)}`);
        if (!res.ok) throw new Error("Impossible de charger la configuration.");
        const data = await res.json();
        if (!isMounted) return;
        setModels(data.models || []);
        setRatios(data.ratios || []);
        setResolutions(data.resolutions || []);
        setDurations(data.durations || [4, 6, 8]);
        setGuide(data.guide || { groups: [], tags: [], presets: [] });
        if (!modelId && data.models?.length) setModelId(data.models[0].id);
      } catch (err) {
        if (isMounted) setConfigError(err.message || "Erreur inconnue.");
      } finally {
        if (isMounted) setLoadingConfig(false);
      }
    }
    loadConfig();
    return () => {
      isMounted = false;
    };
  }, [modelId]);

  useEffect(() => {
    let isMounted = true;
    fetchBillingStatus()
      .then((data) => {
        if (!isMounted) return;
        setBillingStatus(data);
      })
      .catch((err) => {
        if (!isMounted) return;
        setBillingError(err.message || "Erreur billing.");
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    fetchCreditEstimate({
      gen_type: "video",
      model_id: modelId,
      duration_s: duration,
      resolution
    })
      .then((data) => {
        if (!isMounted) return;
        setCreditEstimate(Number(data.credits || 1));
      })
      .catch(() => {
        if (!isMounted) return;
        setCreditEstimate(1);
      });
    return () => {
      isMounted = false;
    };
  }, [modelId, duration, resolution]);

  useEffect(() => {
    let isMounted = true;
    async function loadMediaThumbs() {
      try {
        const res = await fetch("/api/fast/media/assets");
        if (!res.ok) throw new Error("Impossible de charger les medias.");
        const data = await res.json();
        const items = Array.isArray(data.items) ? data.items : [];
        const map = {};
        items.forEach((item) => {
          if (item.url && item.thumbnail_url) map[item.url] = item.thumbnail_url;
        });
        if (isMounted) setMediaThumbs(map);
      } catch {
        if (isMounted) setMediaThumbs({});
      }
    }
    loadMediaThumbs();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    try {
      const stored = window.sessionStorage.getItem("contentos_active_project_id");
      if (stored) setActiveProjectId(stored);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadActiveProject() {
      try {
        const res = await fetch("/api/fast/user/settings");
        if (!res.ok) return;
        const data = await res.json();
        if (isMounted) setActiveProjectId(data.active_project_id || "");
        if (isMounted && data.video_default_mode) {
          setExpertMode(data.video_default_mode);
        }
      } catch {
        // ignore
      }
    }
    loadActiveProject();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadRecentVideos() {
      setRecentLoading(true);
      try {
        const params = new URLSearchParams();
        if (activeProjectId) params.set("project_id", activeProjectId);
        const query = params.toString();
        const res = await fetch(`/api/fast/media/recent/videos${query ? `?${query}` : ""}`);
        if (!res.ok) throw new Error("Impossible de charger les videos recentes.");
        const data = await res.json();
        if (isMounted) setRecentVideos(Array.isArray(data.items) ? data.items : []);
      } catch {
        if (isMounted) setRecentVideos([]);
      } finally {
        if (isMounted) setRecentLoading(false);
      }
    }
    loadRecentVideos();
    return () => {
      isMounted = false;
    };
  }, [activeProjectId, resultUrl]);

  useEffect(() => {
    let isMounted = true;
    async function loadFavoriteTags() {
      setFavoriteTagsLoading(true);
      try {
        const res = await fetch("/api/fast/user/tag-favorites");
        if (!res.ok) throw new Error("Impossible de charger les tags favoris.");
        const data = await res.json();
        if (isMounted) setFavoriteTags(Array.isArray(data.tag_ids) ? data.tag_ids : []);
      } catch {
        if (isMounted) setFavoriteTags([]);
      } finally {
        if (isMounted) setFavoriteTagsLoading(false);
      }
    }
    loadFavoriteTags();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadUserPresets() {
      setUserPresetsLoading(true);
      try {
        const res = await fetch("/api/fast/nano/presets?target=video");
        if (!res.ok) throw new Error("Impossible de charger les presets.");
        const data = await res.json();
        if (isMounted) setUserPresets(Array.isArray(data.presets) ? data.presets : []);
      } catch {
        if (isMounted) setUserPresets([]);
      } finally {
        if (isMounted) setUserPresetsLoading(false);
      }
    }
    loadUserPresets();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadProjectTags() {
      if (!activeProjectId) {
        if (isMounted) setProjectTags([]);
        return;
      }
      setProjectTagsLoading(true);
      try {
        const res = await fetch(`/api/fast/projects/tags?project_id=${encodeURIComponent(activeProjectId)}`);
        if (!res.ok) throw new Error("Impossible de charger les tags projet.");
        const data = await res.json();
        if (isMounted) setProjectTags(Array.isArray(data.tag_ids) ? data.tag_ids : []);
      } catch {
        if (isMounted) setProjectTags([]);
      } finally {
        if (isMounted) setProjectTagsLoading(false);
      }
    }
    loadProjectTags();
    return () => {
      isMounted = false;
    };
  }, [activeProjectId]);

  useEffect(() => {
    if (!activeProjectId) return;
    if (appliedProjectRef.current === activeProjectId) return;
    setSelectedTags(new Set(projectTags));
    appliedProjectRef.current = activeProjectId;
  }, [projectTags, activeProjectId]);

  useEffect(() => {
    const stored = {
      mode,
      modelId,
      ratio,
      resolution,
      duration,
      promptText,
      selectedTags: Array.from(selectedTags),
      selectedPresets,
      grain,
      contrast,
      expertMode,
      narrative,
      cinematography,
      visualStyle,
      constraints,
      inputImage,
      referenceImages,
      startImage,
      endImage,
      inputVideo,
      grokEditVideoUrl,
      inputMediaVersionIdsByTarget,
      baseMediaItemId,
      baseMediaVersionId,
      resultUrl,
      compiledPrompt
    };
    try {
      window.sessionStorage.setItem("contentos_video_state", JSON.stringify(stored));
    } catch {
      // ignore
    }
  }, [
    mode,
    modelId,
    ratio,
    resolution,
    duration,
    promptText,
    selectedTags,
    selectedPresets,
    grain,
    contrast,
    expertMode,
    narrative,
    cinematography,
    visualStyle,
    constraints,
    inputImage,
    referenceImages,
    startImage,
    endImage,
    inputVideo,
    grokEditVideoUrl,
    inputMediaVersionIdsByTarget,
    baseMediaItemId,
    baseMediaVersionId,
    resultUrl,
    compiledPrompt
  ]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("media_picker_done") !== "1") return;
    const target = params.get("target") || "";
    const clearSelection = async () => {
      try {
        await fetch("/api/fast/media/selection", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ image_paths: [], target })
        });
      } catch {
        // Ignore errors clearing selection.
      }
    };
    let handled = false;
    try {
      const raw = window.sessionStorage.getItem("contentos_media_picker_payload");
      if (raw) {
        const payload = JSON.parse(raw);
        if (payload?.target === target && Array.isArray(payload.images)) {
          const urls = payload.images;
          const versions = Array.isArray(payload.versions) ? payload.versions : [];
          const versionIds = versions.map((item) => item.mediaVersionId).filter(Boolean);
          if (versionIds.length) {
            setInputMediaVersionIdsByTarget((prev) => ({ ...prev, [target]: versionIds }));
          }
          const baseItem = versions[0] || {};
          if (baseItem.mediaItemId && ["veo_input", "veo_video", "veo_interp_start"].includes(target)) {
            setBaseMediaItemId(baseItem.mediaItemId || "");
            setBaseMediaVersionId(baseItem.mediaVersionId || "");
          }
          if (target === "veo_input") setInputImage(urls[0] || "");
          if (target === "veo_ref") setReferenceImages(urls.slice(0, 3));
          if (target === "veo_interp_start") setStartImage(urls[0] || "");
          if (target === "veo_interp_end") setEndImage(urls[0] || "");
          if (target === "veo_video") setInputVideo(urls[0] || "");
          window.sessionStorage.removeItem("contentos_media_picker_payload");
          handled = true;
        }
      }
    } catch {
      // Ignore storage errors.
    }
    async function consumeSelection() {
      try {
        if (handled) return;
        const res = await fetch("/api/fast/media/selection");
        if (!res.ok) return;
        const data = await res.json();
        const selection = data.selection || {};
        if (selection.target && selection.target !== target) return;
        const urls = Array.isArray(selection.images) ? selection.images : [];
        if (urls.length) {
          setInputMediaVersionIdsByTarget((prev) => ({ ...prev, [target]: [] }));
        }
        if (target === "veo_input") setInputImage(urls[0] || "");
        if (target === "veo_ref") setReferenceImages(urls.slice(0, 3));
        if (target === "veo_interp_start") setStartImage(urls[0] || "");
        if (target === "veo_interp_end") setEndImage(urls[0] || "");
        if (target === "veo_video") setInputVideo(urls[0] || "");
      } finally {
        await clearSelection();
        params.delete("media_picker_done");
        params.delete("target");
        const base = window.location.pathname;
        const query = params.toString();
        window.history.replaceState({}, "", query ? `${base}?${query}` : base);
      }
    }
    consumeSelection();
  }, []);

  useEffect(() => {
    const forcedAspect = mode === "ref" ? "16:9" : "";
    if (forcedAspect) setRatio(forcedAspect);
  }, [mode]);

  useEffect(() => {
    const forcedResolution = mode === "extend" ? "720p" : "";
    if (forcedResolution) setResolution(forcedResolution);
  }, [mode]);

  useEffect(() => {
    const constrained = resolution === "1080p" || ["interp", "extend", "ref"].includes(mode);
    if (constrained) setDuration(8);
  }, [mode, resolution]);

  useEffect(() => {
    if (!frameEditMode || !frameUrl) return () => {};
    const handleResize = () => updateFrameMeta();
    const timer = window.setTimeout(updateFrameMeta, 0);
    window.addEventListener("resize", handleResize);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
    };
  }, [frameEditMode, frameUrl]);

  useEffect(() => {
    if (!frameUrl) return;
    setFrameRotation(0);
    setFrameContrast(100);
    setFrameCropMode(false);
    setFrameCropRect({ x: 0, y: 0, w: 0, h: 0 });
  }, [frameUrl]);

  useEffect(() => {
    if (!frameCropMode || frameCropRect.w || frameCropRect.h) return;
    if (!frameMeta.displayW || !frameMeta.displayH) return;
    const w = Math.max(frameMeta.displayW * 0.7, 120);
    const h = Math.max(frameMeta.displayH * 0.7, 120);
    setFrameCropRect(
      clampFrameCropRect({
        x: Math.max((frameMeta.displayW - w) / 2, 0),
        y: Math.max((frameMeta.displayH - h) / 2, 0),
        w,
        h
      })
    );
  }, [frameCropMode, frameCropRect.w, frameCropRect.h, frameMeta.displayW, frameMeta.displayH]);

  function toggleTag(tagId) {
    setSelectedTags((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  }

  function applyPreset(preset) {
    setSelectedTags(new Set(preset.tag_ids));
    setSelectedPresets([preset.id]);
  }

  function applyUserPreset(preset) {
    if (preset.tag_ids?.length) setSelectedTags(new Set(preset.tag_ids));
    if (preset.prompt_text) setPromptText(preset.prompt_text);
    if (preset.model_id) setModelId(preset.model_id);
    if (preset.aspect_ratio) setRatio(preset.aspect_ratio);
    if (preset.image_size) setResolution(preset.image_size);
    if (typeof preset.grain === "number") setGrain(preset.grain);
    if (typeof preset.contrast === "number") setContrast(preset.contrast);
  }

  async function saveUserPreset() {
    if (!presetName.trim()) {
      setErrorMsg("Veuillez entrer un nom pour le preset.");
      return;
    }
    try {
      const res = await fetch("/api/fast/nano/presets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: presetName.trim(),
          target: "video",
          tag_ids: Array.from(selectedTags),
          prompt_text: promptText,
          model_id: modelId,
          grain,
          contrast,
          aspect_ratio: ratio,
          image_size: resolution
        })
      });
      if (!res.ok) throw new Error("Impossible de sauvegarder le preset.");
      const data = await res.json();
      if (data.preset) {
        setUserPresets((prev) => [...prev, data.preset]);
      }
      setPresetName("");
      setShowPresetForm(false);
    } catch (err) {
      setErrorMsg(err.message || "Erreur sauvegarde preset.");
    }
  }

  async function deleteSelectedPresets() {
    if (selectedPresetIds.size === 0) return;
    try {
      const res = await fetch("/api/fast/nano/presets", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ preset_ids: Array.from(selectedPresetIds) })
      });
      if (!res.ok) throw new Error("Impossible de supprimer les presets.");
      setUserPresets((prev) => prev.filter((p) => !selectedPresetIds.has(p.preset_id)));
      setSelectedPresetIds(new Set());
    } catch (err) {
      setErrorMsg(err.message || "Erreur suppression preset.");
    }
  }

  function togglePresetSelection(presetId) {
    setSelectedPresetIds((prev) => {
      const next = new Set(prev);
      if (next.has(presetId)) {
        next.delete(presetId);
      } else {
        next.add(presetId);
      }
      return next;
    });
  }

  function clearTags() {
    setSelectedTags(new Set());
    setSelectedPresets([]);
  }

  function buildFinalPrompt() {
    let output = promptText.trim();
    if (expertMode === "expert") {
      const parts = [
        narrative.subject,
        narrative.action,
        narrative.environment,
        narrative.temporal_progression
      ]
        .map((item) => item.trim())
        .filter(Boolean);
      const cameraParts = [
        cinematography.shot_type,
        cinematography.camera_motion,
        cinematography.lens,
        cinematography.focus,
        cinematography.framing
      ]
        .map((item) => item.trim())
        .filter(Boolean);
      const styleParts = [
        visualStyle.lighting,
        visualStyle.color_palette,
        visualStyle.mood,
        visualStyle.art_direction
      ]
        .map((item) => item.trim())
        .filter(Boolean);
      const constraintsParts = [];
      if (constraints.identity_consistency) constraintsParts.push("identity consistency");
      if (constraints.no_style_drift) constraintsParts.push("no style drift");
      if (constraints.no_character_merge) constraintsParts.push("no character merge");
      output = [
        parts.join(" "),
        cameraParts.length ? `Camera: ${cameraParts.join(", ")}` : "",
        styleParts.length ? `Style: ${styleParts.join(", ")}` : "",
        constraintsParts.length ? `Constraints: ${constraintsParts.join(", ")}` : ""
      ]
        .filter(Boolean)
        .join("\n");
    }
    const markers = Array.from(selectedTags)
      .map((tagId) => markerByTag.get(tagId))
      .filter(Boolean);
    if (markers.length) output = `${output} ${markers.join(" ")}`.trim();
    const tweaks = [];
    if (grain !== 15) tweaks.push(`grain ${grain}`);
    if (contrast !== 60) tweaks.push(`contrast ${contrast}`);
    if (tweaks.length) output = `${output} ${tweaks.join(", ")}`.trim();
    return output;
  }

  async function uploadFiles(files) {
    if (!files.length) return [];
    try {
      const form = new FormData();
      files.forEach((file) => form.append("files", file));
      const res = await fetch("/api/fast/video/uploads", { method: "POST", body: form });
      if (!res.ok) throw new Error("Upload impossible.");
      const data = await res.json();
      return Array.isArray(data.items) ? data.items : [];
    } catch (err) {
      setErrorMsg(err.message || "Erreur upload.");
      return [];
    }
  }

  async function handleUploadSingle(setter, acceptType, event) {
    const file = event.target.files?.[0];
    if (!file) return;
    const uploaded = await uploadFiles([file]);
    const match = uploaded.find((item) => item.type === acceptType) || uploaded[0];
    if (match?.url) setter(match.url);
    event.target.value = "";
  }

  async function handleUploadMultiple(setter, acceptType, event) {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const uploaded = await uploadFiles(files);
    const urls = uploaded.filter((item) => item.type === acceptType).map((item) => item.url);
    if (urls.length) setter((prev) => [...new Set([...(prev || []), ...urls])]);
    event.target.value = "";
  }

  const updateFrameMeta = () => {
    const wrap = frameWrapRef.current;
    const img = frameImgRef.current;
    if (!wrap || !img) return;
    const wrapRect = wrap.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();
    const offsetX = imgRect.left - wrapRect.left;
    const offsetY = imgRect.top - wrapRect.top;
    setFrameMeta({
      naturalW: img.naturalWidth || 0,
      naturalH: img.naturalHeight || 0,
      displayW: imgRect.width || 0,
      displayH: imgRect.height || 0,
      offsetX,
      offsetY
    });
  };

  const clampFrameCropRect = (nextRect) => {
    const minSize = 40;
    const maxW = frameMeta.displayW || 0;
    const maxH = frameMeta.displayH || 0;
    let { x, y, w, h } = nextRect;
    w = Math.max(minSize, Math.min(w, maxW));
    h = Math.max(minSize, Math.min(h, maxH));
    x = Math.max(0, Math.min(x, maxW - w));
    y = Math.max(0, Math.min(y, maxH - h));
    return { x, y, w, h };
  };

  const handleSaveFrameEdits = async () => {
    if (!frameMediaItemId || !frameMediaVersionId) {
      setFrameEditError("Aucun frame editable.");
      return;
    }
    const hasCrop = frameCropRect.w && frameCropRect.h;
    const hasRotate = frameRotation % 360 !== 0;
    const hasContrast = frameContrast !== 100;
    if (!hasCrop && !hasRotate && !hasContrast) {
      setFrameEditError("Aucune modification a appliquer.");
      return;
    }
    setFrameEditSaving(true);
    setFrameEditError("");
    const scaleX = frameMeta.displayW ? frameMeta.naturalW / frameMeta.displayW : 1;
    const scaleY = frameMeta.displayH ? frameMeta.naturalH / frameMeta.displayH : 1;
    const payload = {
      media_item_id: frameMediaItemId,
      media_version_id: frameMediaVersionId,
      rotate_deg: frameRotation,
      contrast: frameContrast
    };
    if (hasCrop) {
      payload.crop_x = Math.round(frameCropRect.x * scaleX);
      payload.crop_y = Math.round(frameCropRect.y * scaleY);
      payload.crop_width = Math.round(frameCropRect.w * scaleX);
      payload.crop_height = Math.round(frameCropRect.h * scaleY);
    }
    try {
      const res = await fetch("/api/fast/media/transform", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "Erreur transformation media.");
      }
      setFrameUrl(data.url || "");
      setFrameMediaVersionId(data.media_version_id || frameMediaVersionId);
      setFrameRotation(0);
      setFrameContrast(100);
      setFrameCropMode(false);
      setFrameCropRect({ x: 0, y: 0, w: 0, h: 0 });
    } catch (err) {
      setFrameEditError(err.message || "Erreur transformation media.");
    } finally {
      setFrameEditSaving(false);
    }
  };

  async function handleGenerate() {
    const effectivePrompt = expertMode === "expert" ? buildFinalPrompt().trim() : promptText.trim();
    if (!effectivePrompt) return;
    setGenerating(true);
    setErrorMsg("");
    setCompileReport(null);
    setCompiledPrompt("");
    try {
      const storedProjectId = (() => {
        try {
          return window.sessionStorage.getItem("contentos_active_project_id") || "";
        } catch {
          return "";
        }
      })();
      const projectId = activeProjectId || storedProjectId || null;
      if (mode === "text" && isVeo3Model) {
        const compileRes = await fetch("/api/fast/prompt/compile", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_id: projectId,
            model_id: modelId,
            target: "video",
            description_text: expertMode === "expert" ? buildFinalPrompt() : promptText.trim(),
            selected_preset_ids: selectedPresets,
            selected_tag_ids: Array.from(selectedTags),
            overrides: {
              aspect_ratio: ratio,
              resolution,
              duration_seconds: duration,
              grain,
              contrast,
              prompt_data: expertMode === "expert" ? {
                narrative,
                cinematography,
                visual_style: visualStyle,
                constraints,
                fine_tuning: {
                  grain,
                  contrast
                }
              } : null
            }
          })
        });
        const compileData = await compileRes.json();
        if (!compileRes.ok || compileData.error) {
          throw new Error(compileData.error || "Compilation impossible.");
        }
        if (compileData.validation_report?.errors?.length) {
          setCompileReport(compileData);
          if (isVeo3Model) {
            throw new Error("Compilation invalide.");
          }
        }
        setCompileReport(compileData);
        setCompiledPrompt(compileData.compiled_prompt || "");

        const runRes = await fetch("/api/fast/generation/run", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            project_id: projectId,
            model_id: modelId,
            prompt_version_id: compileData.prompt_version_id
          })
        });
        const runData = await runRes.json();
        if (!runRes.ok || !runData.ok) throw new Error(runData.error || "Generation impossible.");
        setResultUrl(runData.url || "");
        if (runData.last_frame_url) {
          setFrameUrl(runData.last_frame_url);
          setFrameMediaItemId(runData.last_frame_media_item_id || "");
          setFrameMediaVersionId(runData.last_frame_media_version_id || "");
        }
        // Notification de succes
        notifyGenerationComplete("video", true, {
          message: "Votre video a ete generee avec succes !",
          url: "/video"
        });
      } else {
        if (expertMode === "beginner" && isVeo3Model) {
          const inputMediaVersionIds = Object.values(inputMediaVersionIdsByTarget || {})
            .flat()
            .filter(Boolean);
          const compileRes = await fetch("/api/fast/prompt/compile", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              project_id: projectId,
              model_id: modelId,
              target: "video",
              description_text: promptText.trim(),
              selected_preset_ids: selectedPresets,
              selected_tag_ids: Array.from(selectedTags),
              validate_only: true,
              overrides: {
                aspect_ratio: ratio,
                resolution,
                duration_seconds: duration,
                grain,
                contrast,
                media_inputs: {
                  mode,
                  input_image_url: inputImage || null,
                  reference_image_urls: referenceImages,
                  start_image_url: startImage || null,
                  end_image_url: endImage || null,
                  input_video_url: isGrokVideoModel ? (grokEditVideoUrl || null) : (inputVideo || null),
                  input_media_version_ids: inputMediaVersionIds,
                  base_media_item_id: baseMediaItemId || null,
                  base_media_version_id: baseMediaVersionId || null
                }
              }
            })
          });
          const compileData = await compileRes.json();
          if (!compileRes.ok || compileData.error) {
            throw new Error(compileData.error || "Compilation impossible.");
          }
          if (compileData.validation_report?.errors?.length) {
            setCompileReport(compileData);
            throw new Error("Compilation invalide.");
          }
          setCompileReport(compileData);
          setCompiledPrompt(compileData.compiled_prompt || "");
        }
        const inputMediaVersionIds = Object.values(inputMediaVersionIdsByTarget || {})
          .flat()
          .filter(Boolean);
        const payload = {
          mode,
          prompt: effectivePrompt,
          model_id: modelId,
          aspect_ratio: ratio,
          resolution,
          duration_seconds: duration,
          grain,
          contrast,
          project_id: projectId,
          input_image_url: inputImage || null,
          reference_image_urls: referenceImages,
          start_image_url: startImage || null,
          end_image_url: endImage || null,
          input_video_url: isGrokVideoModel ? (grokEditVideoUrl || null) : (inputVideo || null),
          prompt_initial: expertMode === "expert" ? effectivePrompt : promptText.trim(),
          prompt_final: effectivePrompt,
          tags: Array.from(selectedTags),
          input_media_version_ids: inputMediaVersionIds,
          base_media_item_id: baseMediaItemId || null,
          base_media_version_id: baseMediaVersionId || null
        };
        const res = await fetch("/api/fast/video/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok || !data.ok) throw new Error(data.error || "Generation impossible.");
        setResultUrl(data.url || "");
        if (data.last_frame_url) {
          setFrameUrl(data.last_frame_url);
          setFrameMediaItemId(data.last_frame_media_item_id || "");
          setFrameMediaVersionId(data.last_frame_media_version_id || "");
        }
        // Notification de succes
        notifyGenerationComplete("video", true, {
          message: "Votre video a ete generee avec succes !",
          url: "/video"
        });
      }
    } catch (err) {
      setErrorMsg(err.message || "Erreur generation.");
      // Notification d'echec
      notifyGenerationComplete("video", false, {
        error: err.message || "Erreur lors de la generation"
      });
    } finally {
      setGenerating(false);
    }
  }

  async function handleValidate(overrides = {}) {
    const effectivePrompt = expertMode === "expert" ? buildFinalPrompt().trim() : promptText.trim();
    if (!effectivePrompt) return;
    setGenerating(true);
    setErrorMsg("");
    setCompileReport(null);
    setCompiledPrompt("");
    try {
      const storedProjectId = (() => {
        try {
          return window.sessionStorage.getItem("contentos_active_project_id") || "";
        } catch {
          return "";
        }
      })();
      const projectId = activeProjectId || storedProjectId || null;
      const inputMediaVersionIds = Object.values(inputMediaVersionIdsByTarget || {})
        .flat()
        .filter(Boolean);
      const compileRes = await fetch("/api/fast/prompt/compile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          project_id: projectId,
          model_id: modelId,
          target: "video",
          description_text: expertMode === "expert" ? buildFinalPrompt() : promptText.trim(),
          selected_preset_ids: selectedPresets,
          selected_tag_ids: Array.from(selectedTags),
          validate_only: true,
          overrides: {
            aspect_ratio: ratio,
            resolution,
            duration_seconds: duration,
            grain: overrides.grain ?? grain,
            contrast: overrides.contrast ?? contrast,
            media_inputs: {
              mode,
              input_image_url: inputImage || null,
              reference_image_urls: referenceImages,
              start_image_url: startImage || null,
              end_image_url: endImage || null,
            input_video_url: isGrokVideoModel ? (grokEditVideoUrl || null) : (inputVideo || null),
              input_media_version_ids: inputMediaVersionIds,
              base_media_item_id: baseMediaItemId || null,
              base_media_version_id: baseMediaVersionId || null
            },
            prompt_data: expertMode === "expert" ? {
              narrative,
              cinematography: overrides.cinematography ?? cinematography,
              visual_style: visualStyle,
              constraints,
              fine_tuning: {
                grain: overrides.grain ?? grain,
                contrast: overrides.contrast ?? contrast
              }
            } : null
          }
        })
      });
      const compileData = await compileRes.json();
      if (!compileRes.ok || compileData.error) {
        throw new Error(compileData.error || "Validation impossible.");
      }
      setCompileReport(compileData);
      setCompiledPrompt(compileData.compiled_prompt || "");
    } catch (err) {
      setErrorMsg(err.message || "Erreur validation.");
    } finally {
      setGenerating(false);
    }
  }

  async function applyVariation(type) {
    let nextGrain = grain;
    let nextContrast = contrast;
    let nextCine = cinematography;
    if (type === "grain") {
      nextGrain = Math.min(100, grain + 10);
      setGrain(nextGrain);
    } else if (type === "contrast") {
      nextContrast = Math.min(100, contrast + 10);
      setContrast(nextContrast);
    } else if (type === "camera") {
      nextCine = {
        ...cinematography,
        camera_motion: cinematography.camera_motion === "slow zoom in" ? "smooth dolly" : "slow zoom in"
      };
      setCinematography(nextCine);
    }
    await handleValidate({
      grain: nextGrain,
      contrast: nextContrast,
      cinematography: nextCine
    });
  }

  function applyGenerationConfig(item) {
    const generation = item?.generation || {};
    const params = generation.parameters || {};
    const promptVersion = item?.prompt_version || null;
    const finalPrompt =
      generation.compiled_prompt ||
      params.prompt_final ||
      generation.prompt ||
      "";
    setMode(params.mode || "text");
    if (generation.model) setModelId(generation.model);
    if (params.aspect_ratio) setRatio(params.aspect_ratio);
    if (params.resolution) setResolution(params.resolution);
    if (params.duration_seconds) setDuration(Number(params.duration_seconds));
    if (typeof params.grain === "number") setGrain(params.grain);
    if (typeof params.contrast === "number") setContrast(params.contrast);
    const tagIds = promptVersion?.selections?.tag_ids || params.tags || [];
    setSelectedTags(new Set(tagIds));
    const presetIds = promptVersion?.selections?.preset_ids || [];
    setSelectedPresets(presetIds);
    setInputImage(params.input_image_url || "");
    setReferenceImages(params.reference_image_urls || []);
    setStartImage(params.start_image_url || "");
    setEndImage(params.end_image_url || "");
    setInputVideo(params.input_video_url || "");
    if (expertMode === "expert") {
      if (promptVersion?.prompt_data) {
        const pd = promptVersion.prompt_data || {};
        const narrativeData = pd.narrative || {};
        const cineData = pd.cinematography || {};
        const styleData = pd.visual_style || {};
        const constraintData = pd.constraints || {};
        setNarrative({
          subject: narrativeData.subject || "",
          action: narrativeData.action || "",
          environment: narrativeData.environment || "",
          temporal_progression: narrativeData.temporal_progression || ""
        });
        setCinematography({
          shot_type: cineData.shot_type || "",
          camera_motion: cineData.camera_motion || "",
          lens: cineData.lens || "",
          focus: cineData.focus || "",
          framing: cineData.framing || ""
        });
        setVisualStyle({
          lighting: styleData.lighting || "",
          color_palette: styleData.color_palette || "",
          mood: styleData.mood || "",
          art_direction: styleData.art_direction || ""
        });
        setConstraints({
          identity_consistency: constraintData.identity_consistency ?? true,
          no_style_drift: constraintData.no_style_drift ?? true,
          no_character_merge: constraintData.no_character_merge ?? true
        });
      } else {
        setNarrative({
          subject: "",
          action: finalPrompt,
          environment: "",
          temporal_progression: ""
        });
        setCinematography({
          shot_type: "",
          camera_motion: "",
          lens: "",
          focus: "",
          framing: ""
        });
        setVisualStyle({
          lighting: "",
          color_palette: "",
          mood: "",
          art_direction: ""
        });
      }
    } else {
      setPromptText(finalPrompt);
    }
    setCompileReport(null);
    setCompiledPrompt("");
  }

  function applyRecommendedConfig(item) {
    const generation = item?.generation || {};
    const params = generation.parameters || {};
    const promptVersion = item?.prompt_version || null;
    const recommended =
      promptVersion?.recommended ||
      promptVersion?.validation_report?.ai_recommended ||
      null;
    if (!recommended) {
      setErrorMsg("Prompt recommande indisponible.");
      return;
    }
    const finalPrompt =
      generation.compiled_prompt ||
      params.prompt_final ||
      generation.prompt ||
      "";
    setExpertMode("expert");
    setMode(params.mode || "text");
    if (generation.model) setModelId(generation.model);
    if (params.aspect_ratio) setRatio(params.aspect_ratio);
    if (params.resolution) setResolution(params.resolution);
    if (params.duration_seconds) setDuration(Number(params.duration_seconds));
    const fineTuning = recommended.fine_tuning || {};
    if (typeof fineTuning.grain === "number") setGrain(fineTuning.grain);
    else if (typeof params.grain === "number") setGrain(params.grain);
    if (typeof fineTuning.contrast === "number") setContrast(fineTuning.contrast);
    else if (typeof params.contrast === "number") setContrast(params.contrast);
    const suggestedTags =
      Array.isArray(recommended.tags_suggeres) && recommended.tags_suggeres.length
        ? recommended.tags_suggeres
        : promptVersion?.selections?.tag_ids || params.tags || [];
    setSelectedTags(new Set(suggestedTags));
    const presetIds = promptVersion?.selections?.preset_ids || [];
    setSelectedPresets(presetIds);
    setInputImage(params.input_image_url || "");
    setReferenceImages(params.reference_image_urls || []);
    setStartImage(params.start_image_url || "");
    setEndImage(params.end_image_url || "");
    setInputVideo(params.input_video_url || "");
    const narrativeData = recommended.narrative || {};
    const cineData = recommended.cinematography || {};
    const styleData = recommended.visual_style || {};
    const constraintData = recommended.constraints || {};
    setNarrative({
      subject: narrativeData.subject || "",
      action: narrativeData.action || finalPrompt,
      environment: narrativeData.environment || "",
      temporal_progression: narrativeData.temporal_progression || ""
    });
    setCinematography({
      shot_type: cineData.shot_type || "",
      camera_motion: cineData.camera_motion || "",
      lens: cineData.lens || "",
      focus: cineData.focus || "",
      framing: cineData.framing || ""
    });
    setVisualStyle({
      lighting: styleData.lighting || "",
      color_palette: styleData.color_palette || "",
      mood: styleData.mood || "",
      art_direction: styleData.art_direction || ""
    });
    setConstraints({
      identity_consistency: constraintData.identity_consistency ?? true,
      no_style_drift: constraintData.no_style_drift ?? true,
      no_character_merge: constraintData.no_character_merge ?? true
    });
    setPromptText(finalPrompt);
    setCompileReport(null);
    setCompiledPrompt("");
    setErrorMsg("");
  }

  async function saveProjectTags(nextTags) {
    if (!activeProjectId) return;
    try {
      const res = await fetch("/api/fast/projects/tags", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: activeProjectId, tag_ids: nextTags })
      });
      if (!res.ok) throw new Error("Impossible d'enregistrer les tags du projet.");
      const data = await res.json();
      setProjectTags(Array.isArray(data.tag_ids) ? data.tag_ids : nextTags);
    } catch (err) {
      setErrorMsg(err.message || "Erreur tags projet.");
    }
  }

  async function saveFavoriteTags(nextTags) {
    try {
      const res = await fetch("/api/fast/user/tag-favorites", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tag_ids: nextTags })
      });
      if (!res.ok) throw new Error("Impossible d'enregistrer les tags favoris.");
      const data = await res.json();
      setFavoriteTags(Array.isArray(data.tag_ids) ? data.tag_ids : nextTags);
    } catch (err) {
      setErrorMsg(err.message || "Erreur tags favoris.");
    }
  }

  function addSelectionToProject() {
    if (!activeProjectId) return;
    const next = Array.from(new Set([...projectTags, ...Array.from(selectedTags)]));
    saveProjectTags(next);
  }

  function removeProjectTag(tagId) {
    const next = projectTags.filter((tag) => tag !== tagId);
    saveProjectTags(next);
  }

  function clearProjectTags() {
    saveProjectTags([]);
  }

  function addSelectionToFavorites() {
    const next = Array.from(new Set([...favoriteTags, ...Array.from(selectedTags)]));
    saveFavoriteTags(next);
  }

  function removeFavoriteTag(tagId) {
    const next = favoriteTags.filter((tag) => tag !== tagId);
    saveFavoriteTags(next);
  }

  function clearFavoriteTags() {
    saveFavoriteTags([]);
  }

  function applyAiRecommended() {
    if (!aiRecommended) return;
    setExpertMode("expert");
    const fineTuning = aiRecommended.fine_tuning || {};
    if (typeof fineTuning.grain === "number") setGrain(fineTuning.grain);
    if (typeof fineTuning.contrast === "number") setContrast(fineTuning.contrast);
    const suggestedTags =
      Array.isArray(aiRecommended.tags_suggeres) && aiRecommended.tags_suggeres.length
        ? aiRecommended.tags_suggeres
        : Array.isArray(aiRecommended.suggested_tags) && aiRecommended.suggested_tags.length
          ? aiRecommended.suggested_tags
          : [];
    if (suggestedTags.length) setSelectedTags(new Set(suggestedTags));
    const narrativeData = aiRecommended.narrative || {};
    const cineData = aiRecommended.cinematography || {};
    const styleData = aiRecommended.visual_style || {};
    const constraintData = aiRecommended.constraints || {};
    setNarrative({
      subject: narrativeData.subject || "",
      action: narrativeData.action || "",
      environment: narrativeData.environment || "",
      temporal_progression: narrativeData.temporal_progression || ""
    });
    setCinematography({
      shot_type: cineData.shot_type || "",
      camera_motion: cineData.camera_motion || "",
      lens: cineData.lens || "",
      focus: cineData.focus || "",
      framing: cineData.framing || ""
    });
    setVisualStyle({
      lighting: styleData.lighting || "",
      color_palette: styleData.color_palette || "",
      mood: styleData.mood || "",
      art_direction: styleData.art_direction || ""
    });
    setConstraints({
      identity_consistency: constraintData.identity_consistency ?? true,
      no_style_drift: constraintData.no_style_drift ?? true,
      no_character_merge: constraintData.no_character_merge ?? true
    });
  }

  const inputImageEntries = useMemo(
    () => (inputImage ? [{ url: inputImage, thumbnail_url: mediaThumbs[inputImage] || "" }] : []),
    [inputImage, mediaThumbs]
  );
  const referenceEntries = useMemo(
    () => referenceImages.map((url) => ({ url, thumbnail_url: mediaThumbs[url] || "" })),
    [referenceImages, mediaThumbs]
  );
  const startEntries = useMemo(
    () => (startImage ? [{ url: startImage, thumbnail_url: mediaThumbs[startImage] || "" }] : []),
    [startImage, mediaThumbs]
  );
  const endEntries = useMemo(
    () => (endImage ? [{ url: endImage, thumbnail_url: mediaThumbs[endImage] || "" }] : []),
    [endImage, mediaThumbs]
  );
  const finalPrompt = compiledPrompt || buildFinalPrompt();
  const activeTags = Array.from(selectedTags).map((id) => guide.tags.find((t) => t.id === id)).filter(Boolean);
  const constrainedDuration = resolution === "1080p" || ["interp", "extend", "ref"].includes(mode);
  const isGrokVideoModel = String(modelId || "").includes("grok-imagine-video");
  const isVeo3Model = String(modelId || "").includes("veo-3");
  const effectiveResolutions = isGrokVideoModel
    ? [
      { label: "480p", value: "480p" },
      { label: "720p", value: "720p" },
    ]
    : resolutions;
  const effectiveDurationRange = isGrokVideoModel
    ? { min: 5, max: 15, step: 1 }
    : { min: Math.min(...durations), max: Math.max(...durations), step: 2 };

  useEffect(() => {
    if (!isGrokVideoModel) return;
    if (resolution !== "480p" && resolution !== "720p") {
      setResolution("720p");
    }
  }, [isGrokVideoModel, resolution]);

  useEffect(() => {
    if (isGrokVideoModel) {
      if (duration < 5 || duration > 15) setDuration(5);
    } else {
      const max = Math.max(...durations);
      if (duration > max) setDuration(max);
    }
  }, [isGrokVideoModel]);
  const creditsRemaining = billingStatus?.credits_remaining;
  const creditsNeeded = Number.isFinite(creditEstimate) ? creditEstimate : 1;
  const hasCredits = typeof creditsRemaining === "number" ? creditsRemaining >= creditsNeeded : true;
  const missingCredits =
    typeof creditsRemaining === "number" ? Math.max(creditsNeeded - creditsRemaining, 0) : 0;
  const previewStyle = {
    transform: `scale(${previewZoom})`
  };
  const frameCropInset =
    frameCropMode && frameMeta.displayW && frameMeta.displayH
      ? `inset(${(frameCropRect.y / frameMeta.displayH) * 100}% ${((frameMeta.displayW - frameCropRect.x - frameCropRect.w) / frameMeta.displayW) * 100}% ${((frameMeta.displayH - frameCropRect.y - frameCropRect.h) / frameMeta.displayH) * 100}% ${(frameCropRect.x / frameMeta.displayW) * 100}%)`
      : "none";
  const frameStyle = {
    transform: `rotate(${frameRotation}deg)`,
    filter: `contrast(${frameContrast}%)`,
    clipPath: frameCropInset
  };
  const hasFrameEditChanges =
    frameRotation % 360 !== 0 ||
    frameContrast !== 100 ||
    Boolean(frameCropRect.w && frameCropRect.h);
  const mediaOk =
    mode === "image"
      ? inputImageEntries.length >= 1
      : mode === "ref"
        ? referenceEntries.length >= 3
        : mode === "interp"
          ? startEntries.length >= 1 && endEntries.length >= 1
          : mode === "extend"
            ? Boolean(inputVideo)
            : true;
  const canSubmit = (expertMode === "expert" ? buildFinalPrompt().trim() : promptText.trim()) && mediaOk;
  const aiRecommended = compileReport?.validation_report?.ai_recommended || null;

  const handleSingleSelection = (target, setter) => (entries) => {
    const next = Array.isArray(entries) ? entries : [];
    const url = next[0]?.url || "";
    setter(url);
    setInputMediaVersionIdsByTarget((prev) => ({
      ...prev,
      [target]: next.map((item) => item.mediaVersionId).filter(Boolean)
    }));
    if (next[0]?.mediaItemId && ["veo_input", "veo_interp_start"].includes(target)) {
      setBaseMediaItemId(next[0].mediaItemId || "");
      setBaseMediaVersionId(next[0].mediaVersionId || "");
    }
  };

  const handleMultiSelection = (target, setter, limit) => (entries) => {
    const next = Array.isArray(entries) ? entries.slice(0, limit) : [];
    setter(next.map((item) => item.url).filter(Boolean));
    setInputMediaVersionIdsByTarget((prev) => ({
      ...prev,
      [target]: next.map((item) => item.mediaVersionId).filter(Boolean)
    }));
  };

  return (
    <div className="app-shell">
      <Sidebar active="video" />
      <div className="app-main">
        <div className="video-page">
          <header className="video-header">
            <div>
              <p className="video-kicker">Studio Video</p>
              <h1 className="video-title page-title-accent">Generation Video Guidee</h1>
              <p className="video-subtitle">Controle technique a gauche, creative a droite.</p>
            </div>
            <div className="video-header-actions">
              <button className="video-pill" type="button" onClick={() => (window.location.href = "/projects")}>
                <span className="material-symbols-rounded">arrow_back</span>
                Retour projets
              </button>
              <button className="video-pill accent" type="button">
                Credits: {typeof creditsRemaining === "number" ? creditsRemaining : "--"}
              </button>
            </div>
          </header>

          {errorMsg && <div className="video-alert error">{errorMsg}</div>}
          {frameEditError && <div className="video-alert error">{frameEditError}</div>}

          <div className="video-layout">
            <section className="video-canvas-area">
              <div className="video-canvas-toolbar">
                <div className="video-toolgroup">
                  <button
                    className="video-tool"
                    type="button"
                    onClick={() => setPreviewZoom((prev) => Math.max(0.8, prev - 0.1))}
                    disabled={previewZoom <= 0.8}
                  >
                    <span className="material-symbols-rounded">zoom_out</span>
                  </button>
                  <button
                    className="video-tool"
                    type="button"
                    onClick={() => setPreviewZoom((prev) => Math.min(1.8, prev + 0.1))}
                    disabled={previewZoom >= 1.8}
                  >
                    <span className="material-symbols-rounded">zoom_in</span>
                  </button>
                  <button className="video-tool" type="button" onClick={() => setPreviewZoom(1)}>
                    <span className="material-symbols-rounded">fit_screen</span>
                    Fit
                  </button>
                </div>
                <div className="video-toolgroup">
                  <button
                    className={`video-tool ${frameEditMode ? "active" : ""}`}
                    type="button"
                    onClick={() => setFrameEditMode((prev) => !prev)}
                    disabled={!frameUrl}
                  >
                    <span className="material-symbols-rounded">crop_free</span>
                    Edit frame
                  </button>
                  <button
                    className="video-tool"
                    type="button"
                    onClick={handleSaveFrameEdits}
                    disabled={
                      !frameEditMode ||
                      !frameMediaItemId ||
                      !frameMediaVersionId ||
                      frameEditSaving ||
                      !hasFrameEditChanges
                    }
                  >
                    <span className="material-symbols-rounded">done</span>
                    {frameEditSaving ? "Sauvegarde..." : "Valider"}
                  </button>
                </div>
                <div className="video-toolgroup">
                  <span className="video-muted">Zoom timeline</span>
                </div>
                {frameEditMode && (
                  <div className="video-toolgroup">
                    <button
                      className="video-tool"
                      type="button"
                      onClick={() => setFrameRotation((prev) => (prev - 90 + 360) % 360)}
                    >
                      <span className="material-symbols-rounded">rotate_left</span>
                    </button>
                    <button
                      className="video-tool"
                      type="button"
                      onClick={() => setFrameRotation((prev) => (prev + 90) % 360)}
                    >
                      <span className="material-symbols-rounded">rotate_right</span>
                    </button>
                    <button
                      className={`video-tool ${frameCropMode ? "active" : ""}`}
                      type="button"
                      onClick={() => setFrameCropMode((prev) => !prev)}
                    >
                      <span className="material-symbols-rounded">crop</span>
                      Recadrage
                    </button>
                    <label className="video-contrast">
                      Contraste
                      <input
                        type="range"
                        min="60"
                        max="140"
                        value={frameContrast}
                        onChange={(event) => setFrameContrast(Number(event.target.value))}
                      />
                    </label>
                  </div>
                )}
              </div>
              <div className="video-canvas-stage">
                {frameEditMode && frameUrl ? (
                  <div className="video-preview" ref={frameWrapRef}>
                    <img
                      ref={frameImgRef}
                      src={frameUrl}
                      alt="Frame"
                      style={frameStyle}
                      onLoad={updateFrameMeta}
                    />
                    {frameCropMode && frameMeta.displayW > 0 && frameMeta.displayH > 0 && (
                      <div
                        className="media-edit-crop"
                        style={{
                          left: frameMeta.offsetX + frameCropRect.x,
                          top: frameMeta.offsetY + frameCropRect.y,
                          width: frameCropRect.w,
                          height: frameCropRect.h
                        }}
                        onPointerDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          frameCropDragRef.current = {
                            handle: "move",
                            startX: event.clientX,
                            startY: event.clientY,
                            rect: { ...frameCropRect }
                          };
                          const onMove = (moveEvent) => {
                            const payload = frameCropDragRef.current;
                            if (!payload) return;
                            const dx = moveEvent.clientX - payload.startX;
                            const dy = moveEvent.clientY - payload.startY;
                            setFrameCropRect(
                              clampFrameCropRect({
                                ...payload.rect,
                                x: payload.rect.x + dx,
                                y: payload.rect.y + dy
                              })
                            );
                          };
                          const onUp = () => {
                            frameCropDragRef.current = null;
                            window.removeEventListener("pointermove", onMove);
                            window.removeEventListener("pointerup", onUp);
                          };
                          window.addEventListener("pointermove", onMove);
                          window.addEventListener("pointerup", onUp);
                        }}
                      >
                        {["nw", "ne", "sw", "se"].map((handle) => (
                          <button
                            key={handle}
                            type="button"
                            className={`media-edit-handle ${handle}`}
                            onPointerDown={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              frameCropDragRef.current = {
                                handle,
                                startX: event.clientX,
                                startY: event.clientY,
                                rect: { ...frameCropRect }
                              };
                              const onMove = (moveEvent) => {
                                const payload = frameCropDragRef.current;
                                if (!payload) return;
                                const dx = moveEvent.clientX - payload.startX;
                                const dy = moveEvent.clientY - payload.startY;
                                const minSize = 40;
                                let { x, y, w, h } = payload.rect;
                                if (payload.handle.includes("n")) {
                                  y = y + dy;
                                  h = payload.rect.h - dy;
                                }
                                if (payload.handle.includes("s")) {
                                  h = payload.rect.h + dy;
                                }
                                if (payload.handle.includes("w")) {
                                  x = x + dx;
                                  w = payload.rect.w - dx;
                                }
                                if (payload.handle.includes("e")) {
                                  w = payload.rect.w + dx;
                                }
                                if (w < minSize || h < minSize) return;
                                setFrameCropRect(clampFrameCropRect({ x, y, w, h }));
                              };
                              const onUp = () => {
                                frameCropDragRef.current = null;
                                window.removeEventListener("pointermove", onMove);
                                window.removeEventListener("pointerup", onUp);
                              };
                              window.addEventListener("pointermove", onMove);
                              window.addEventListener("pointerup", onUp);
                            }}
                            aria-label="Redimensionner le cadre"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ) : resultUrl ? (
                  <div className="video-preview">
                    <video src={resultUrl} controls preload="metadata" style={previewStyle} />
                    <button
                      type="button"
                      className="video-download-link"
                      onClick={() => downloadFile(resultUrl, buildDownloadName(resultUrl))}
                      title="Telecharger"
                    >
                      <span className="material-symbols-rounded">download</span>
                    </button>
                  </div>
                ) : (
                  <div className="video-preview-empty">Aucun rendu pour le moment.</div>
                )}
              </div>
              <div className="video-timeline-mini">
                <div className="video-timeline-track">
                  <div className="video-timeline-playhead" />
                </div>
              </div>
            </section>

            <aside className="video-controls">
              <section className="video-card">
                <h3>Source / references</h3>
                <div className="video-media-stack">
                  {mode === "image" && (
                    <MediaSelector
                      title="Image source"
                      selected={inputImageEntries}
                      onChange={handleSingleSelection("veo_input", setInputImage)}
                      maxCount={1}
                      allowLibrary
                      pickerTarget="veo_input"
                      pickerReturn="/video"
                      pickerMulti={false}
                    />
                  )}

                  {mode === "ref" && (
                    <MediaSelector
                      title="Images de reference"
                      selected={referenceEntries}
                      onChange={handleMultiSelection("veo_ref", setReferenceImages, 3)}
                      maxCount={3}
                      minCount={3}
                      allowLibrary
                      pickerTarget="veo_ref"
                      pickerReturn="/video"
                      pickerMulti
                    />
                  )}

                  {mode === "interp" && (
                    <>
                      <MediaSelector
                        title="Image debut"
                        selected={startEntries}
                        onChange={handleSingleSelection("veo_interp_start", setStartImage)}
                        maxCount={1}
                        allowLibrary
                        pickerTarget="veo_interp_start"
                        pickerReturn="/video"
                        pickerMulti={false}
                      />
                      <MediaSelector
                        title="Image fin"
                        selected={endEntries}
                        onChange={handleSingleSelection("veo_interp_end", setEndImage)}
                        maxCount={1}
                        allowLibrary
                        pickerTarget="veo_interp_end"
                        pickerReturn="/video"
                        pickerMulti={false}
                      />
                    </>
                  )}

                  {mode === "extend" && (
                    <div className="video-media-item">
                      <div className="video-media-head">
                        <span>Video source</span>
                        <a
                          className="video-link"
                          href="/media?picker=1&target=veo_video&type=video&multi=0&return=/video"
                        >
                          Bibliotheque
                        </a>
                      </div>
                      <p className="video-muted">
                        Extension Veo: selectionnez une video generee par Veo apres la mise a jour (fichier GenAI requis).
                      </p>
                      <input type="file" accept="video/*" onChange={(e) => handleUploadSingle(setInputVideo, "video", e)} />
                      {inputVideo && (
                        <div className="video-media-preview">
                          <video src={inputVideo} poster={mediaThumbs[inputVideo] || ""} controls preload="metadata" />
                          <button
                            type="button"
                            className="video-media-remove"
                            onClick={() => setInputVideo("")}
                            aria-label="Supprimer"
                          >
                            <span className="material-symbols-rounded">close</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {mode === "text" && (
                    <p className="video-muted">Ce mode n'a pas d'entree media.</p>
                  )}
                </div>
              </section>

              <section className="video-card">
                <h3>Parametres</h3>
                <div className="video-mode-bar">
                  {MODES.map((item) => (
                    <button
                      key={item.id}
                      className={mode === item.id ? "video-mode active" : "video-mode"}
                      onClick={() => setMode(item.id)}
                    >
                      <span className="video-mode-dot" />
                      {item.label}
                    </button>
                  ))}
                </div>
                {loadingConfig ? (
                  <p>{t("common.loading")}</p>
                ) : (
                  <div className="video-config-grid">
                    <label>
                      Modele
                      <select value={modelId} onChange={(e) => setModelId(e.target.value)}>
                        {models.map((model) => (
                          <option key={model.id} value={model.id}>
                            {model.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Format
                      <select value={ratio} onChange={(e) => setRatio(e.target.value)} disabled={mode === "ref"}>
                        {ratios.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      Resolution
                      <select
                        value={resolution}
                        onChange={(e) => setResolution(e.target.value)}
                        disabled={mode === "extend"}
                      >
                        {effectiveResolutions.map((item) => (
                          <option key={item.value} value={item.value}>
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="video-range">
                      Duree (s) <span>{duration}</span>
                      <input
                        type="range"
                        min={effectiveDurationRange.min}
                        max={effectiveDurationRange.max}
                        step={effectiveDurationRange.step}
                        value={duration}
                        disabled={constrainedDuration}
                        onChange={(e) => setDuration(Number(e.target.value))}
                      />
                    </label>
                  </div>
                )}
                {configError && <div className="video-alert error">{configError}</div>}

                <div className="video-mode-toggle">
                  <span>Mode</span>
                  <button
                    className={expertMode === "beginner" ? "video-toggle active" : "video-toggle"}
                    onClick={() => setExpertMode("beginner")}
                  >
                    Debutant
                  </button>
                  <button
                    className={expertMode === "expert" ? "video-toggle active" : "video-toggle"}
                    onClick={() => setExpertMode("expert")}
                  >
                    Expert
                  </button>
                </div>
                {isGrokVideoModel && (
                  <label>
                    Video a editer (URL)
                    <input
                      type="url"
                      placeholder="https://.../video.mp4"
                      value={grokEditVideoUrl}
                      onChange={(e) => setGrokEditVideoUrl(e.target.value)}
                    />
                  </label>
                )}
                {expertMode === "beginner" ? (
                  <div className="video-subpanel">
                    <h3>Votre description</h3>
                    <textarea
                      value={promptText}
                      onChange={(e) => setPromptText(e.target.value)}
                      rows={6}
                      placeholder="Decris ta scene..."
                    />
                  </div>
                ) : (
                  <div className="video-expert-blocks">
                    <div className="video-expert-card">
                      <h3>{t("video.expert.narrative.title")}</h3>
                      <div className="video-form-grid">
                        <input
                          type="text"
                          placeholder={t("video.expert.narrative.subject")}
                          value={narrative.subject}
                          onChange={(e) => setNarrative((prev) => ({ ...prev, subject: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("video.expert.narrative.action")}
                          value={narrative.action}
                          onChange={(e) => setNarrative((prev) => ({ ...prev, action: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("video.expert.narrative.environment")}
                          value={narrative.environment}
                          onChange={(e) => setNarrative((prev) => ({ ...prev, environment: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("video.expert.narrative.temporal")}
                          value={narrative.temporal_progression}
                          onChange={(e) => setNarrative((prev) => ({ ...prev, temporal_progression: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="video-expert-card">
                      <h3>{t("video.expert.camera.title")}</h3>
                      <div className="video-form-grid">
                        <input
                          type="text"
                          placeholder={t("video.expert.camera.shotType")}
                          value={cinematography.shot_type}
                          onChange={(e) => setCinematography((prev) => ({ ...prev, shot_type: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("video.expert.camera.cameraMotion")}
                          value={cinematography.camera_motion}
                          onChange={(e) => setCinematography((prev) => ({ ...prev, camera_motion: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("video.expert.camera.lens")}
                          value={cinematography.lens}
                          onChange={(e) => setCinematography((prev) => ({ ...prev, lens: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("video.expert.camera.focus")}
                          value={cinematography.focus}
                          onChange={(e) => setCinematography((prev) => ({ ...prev, focus: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("video.expert.camera.framing")}
                          value={cinematography.framing}
                          onChange={(e) => setCinematography((prev) => ({ ...prev, framing: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="video-expert-card">
                      <h3>{t("video.expert.style.title")}</h3>
                      <div className="video-form-grid">
                        <input
                          type="text"
                          placeholder={t("video.expert.style.lighting")}
                          value={visualStyle.lighting}
                          onChange={(e) => setVisualStyle((prev) => ({ ...prev, lighting: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("video.expert.style.colorPalette")}
                          value={visualStyle.color_palette}
                          onChange={(e) => setVisualStyle((prev) => ({ ...prev, color_palette: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("video.expert.style.mood")}
                          value={visualStyle.mood}
                          onChange={(e) => setVisualStyle((prev) => ({ ...prev, mood: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder={t("video.expert.style.artDirection")}
                          value={visualStyle.art_direction}
                          onChange={(e) => setVisualStyle((prev) => ({ ...prev, art_direction: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="video-expert-card">
                      <h3>{t("video.expert.constraints.title")}</h3>
                      <div className="video-constraints">
                        <label>
                          <input
                            type="checkbox"
                            checked={constraints.identity_consistency}
                            onChange={(e) =>
                              setConstraints((prev) => ({ ...prev, identity_consistency: e.target.checked }))
                            }
                          />
                          {t("video.expert.constraints.identity")}
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={constraints.no_style_drift}
                            onChange={(e) =>
                              setConstraints((prev) => ({ ...prev, no_style_drift: e.target.checked }))
                            }
                          />
                          {t("video.expert.constraints.noStyleDrift")}
                        </label>
                        <label>
                          <input
                            type="checkbox"
                            checked={constraints.no_character_merge}
                            onChange={(e) =>
                              setConstraints((prev) => ({ ...prev, no_character_merge: e.target.checked }))
                            }
                          />
                          {t("video.expert.constraints.noCharacterMerge")}
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </section>

              <section className="video-card">
                <div className="video-card-header">
                  <h3>Tags & presets</h3>
                  <div className="video-header-actions">
                    {selectedPresetIds.size > 0 && (
                      <button
                        type="button"
                        className="video-secondary"
                        onClick={deleteSelectedPresets}
                        disabled={userPresetsLoading}
                      >
                        Supprimer ({selectedPresetIds.size})
                      </button>
                    )}
                    <button
                      type="button"
                      className="video-secondary"
                      onClick={() => setShowPresetForm(!showPresetForm)}
                    >
                      {showPresetForm ? "Annuler" : "+ Nouveau"}
                    </button>
                  </div>
                </div>
                <div className="video-chip-row">
                  {guide.presets.map((preset) => (
                    <button key={preset.id} className="video-chip preset" onClick={() => applyPreset(preset)}>
                      {preset.label}
                    </button>
                  ))}
                  {userPresets.map((preset) => (
                    <label key={preset.preset_id} className="video-user-preset-chip">
                      <input
                        type="checkbox"
                        checked={selectedPresetIds.has(preset.preset_id)}
                        onChange={() => togglePresetSelection(preset.preset_id)}
                      />
                      <button
                        type="button"
                        className="video-chip preset user"
                        onClick={() => applyUserPreset(preset)}
                      >
                        {preset.name}
                      </button>
                    </label>
                  ))}
                  {!userPresets.length && (
                    <span className="video-muted">Aucun preset utilisateur.</span>
                  )}
                </div>
                {showPresetForm && (
                  <div className="video-form-grid video-preset-form">
                    <input
                      type="text"
                      placeholder="Nom du preset"
                      value={presetName}
                      onChange={(e) => setPresetName(e.target.value)}
                    />
                    <button type="button" className="video-primary" onClick={saveUserPreset}>
                      Sauvegarder
                    </button>
                  </div>
                )}
                <TagManager
                  prefix="video"
                  tagsContainerClass="video-tag-picker"
                  guide={guide}
                  selectedTags={selectedTags}
                  tagLabelById={tagLabelById}
                  projectTags={projectTags}
                  favoriteTags={favoriteTags}
                  activeProjectId={activeProjectId}
                  projectTagsLoading={projectTagsLoading}
                  favoriteTagsLoading={favoriteTagsLoading}
                  onAddSelectionToProject={addSelectionToProject}
                  onAddSelectionToFavorites={addSelectionToFavorites}
                  onClearProjectTags={clearProjectTags}
                  onClearFavoriteTags={clearFavoriteTags}
                  onRemoveProjectTag={removeProjectTag}
                  onRemoveFavoriteTag={removeFavoriteTag}
                  onToggleTag={toggleTag}
                />
              </section>

              <section className="video-card">
                <h3>Reglages fins</h3>
                <div className="video-range-grid">
                  <label className="video-range">
                    Grain <span>{grain}</span>
                    <input type="range" min="0" max="100" value={grain} onChange={(e) => setGrain(Number(e.target.value))} />
                  </label>
                  <label className="video-range">
                    Contraste <span>{contrast}</span>
                    <input type="range" min="0" max="100" value={contrast} onChange={(e) => setContrast(Number(e.target.value))} />
                  </label>
                </div>
              </section>

              <section className="video-card">
                <div className="video-card-header">
                  <h3>Generation</h3>
                </div>
                <div className="video-prompt-actions">
                  <button
                    type="button"
                    className="video-secondary"
                    disabled={!aiRecommended}
                    onClick={applyAiRecommended}
                  >
                    Copier le prompt recommande
                  </button>
                  <div className="video-prompt-actions-right">
                    <button
                      className="video-secondary"
                      disabled={generating || !canSubmit}
                      onClick={handleValidate}
                    >
                      {generating ? "Validation..." : "Valider"}
                    </button>
                    <button
                      className="video-primary"
                      disabled={generating || !canSubmit || !hasCredits}
                      onClick={handleGenerate}
                    >
                      {generating ? "Generation..." : "Generer la video"}
                      <span className="credit-badge">{creditsNeeded}</span>
                    </button>
                  </div>
                </div>
                {billingError && <div className="video-alert error">{billingError}</div>}
                {!hasCredits && missingCredits > 0 && (
                  <button
                    type="button"
                    className="billing-upgrade-link"
                    onClick={() => (window.location.href = "/pricing")}
                  >
                    {missingCredits} / {creditsNeeded} credits manquants rechargez !
                  </button>
                )}
              </section>

              <section className="video-card">
                <h3>Prompt final</h3>
                <div className="video-final">
                  <p>{finalPrompt || "Commence par decrire ta scene."}</p>
                  {activeTags.length > 0 && (
                    <div className="video-final-tags">
                      {activeTags.map((tag) => (
                        <span key={tag.id} className="video-final-tag">
                          {tag.label}
                        </span>
                      ))}
                    </div>
                  )}
                  {compileReport?.validation_report?.warnings?.length > 0 && (
                    <div className="video-final-warnings">
                      {compileReport.validation_report.warnings.map((warn) => (
                        <span key={warn}>{warn}</span>
                      ))}
                    </div>
                  )}
                  {compileReport?.validation_report?.errors?.length > 0 && (
                    <div className="video-final-errors">
                      {compileReport.validation_report.errors.map((err) => (
                        <span key={err}>{err}</span>
                      ))}
                    </div>
                  )}
                  {compileReport?.validation_report?.auto_fixes?.length > 0 && (
                    <div className="video-final-fixes">
                      <span>Auto-fixes: {compileReport.validation_report.auto_fixes.join(", ")}</span>
                    </div>
                  )}
                  {expertMode === "expert" && (
                    <div className="video-final-variations">
                      <span>Variations</span>
                      <div className="video-variation-buttons">
                        <button type="button" onClick={() => applyVariation("grain")}>+ Grain</button>
                        <button type="button" onClick={() => applyVariation("contrast")}>+ Contrast</button>
                        <button type="button" onClick={() => applyVariation("camera")}>+ Camera</button>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              <section className="video-card">
                <h3>Prompt version recommande</h3>
                {aiRecommended ? (
                  <pre className="video-json-block">{JSON.stringify(aiRecommended, null, 2)}</pre>
                ) : (
                  <p className="video-muted">Aucune recommandation disponible.</p>
                )}
              </section>
            </aside>
          </div>

          <section className="video-card video-history">
            <div className="video-card-header">
              <h3>Historique</h3>
            </div>
            {recentLoading && <p className="video-muted">Chargement...</p>}
            {!recentLoading && recentVideos.length === 0 && (
              <p className="video-muted">Aucune video recente.</p>
            )}
            {!recentLoading && recentVideos.length > 0 && (
              <div className="video-recent-list">
                {recentVideos.map((item, index) => {
                  const previewUrl = item?.asset?.thumbnail_url || item?.asset?.url || "";
                  const projectName = item?.asset?.project_name || "";
                  return (
                    <div key={`${item?.generation?.generation_id || "gen"}-${index}`} className="video-recent-item">
                      <div className="video-recent-preview">
                        {previewUrl ? (
                          <img src={previewUrl} alt="Video preview" />
                        ) : (
                          <div className="video-recent-empty">Preview</div>
                        )}
                      </div>
                      <div className="video-recent-meta">
                        <span className="video-recent-title">{projectName || "Sans projet"}</span>
                        <div className="video-recent-actions">
                          <button
                            type="button"
                            className="video-recent-apply"
                            onClick={() => applyGenerationConfig(item)}
                          >
                            Reutiliser config
                          </button>
                          <button
                            type="button"
                            className="video-recent-recommended"
                            disabled={!item?.prompt_version?.recommended && !item?.prompt_version?.validation_report?.ai_recommended}
                            onClick={() => applyRecommendedConfig(item)}
                          >
                            Charger recommande
                          </button>
                          <button
                            type="button"
                            className="video-recent-view"
                            disabled={!item?.asset?.id}
                            onClick={() => {
                              if (!item?.asset?.id) return;
                              window.location.href = `/media?asset_id=${encodeURIComponent(item.asset.id)}`;
                            }}
                          >
                            Voir
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
